// Standalone "Sign in / Create account" button, rendered on its own so the
// marketing partner can iframe it separately from the converter and place it
// anywhere in their layout.
//
// It is the same button as the converter's header — same handler, same styling
// — just without the surrounding page. Auth state is shared with the converter
// frame through localStorage: both frames are same-origin, and AppContext
// listens for the `storage` event so a sign-in here updates the converter frame
// (see the sync effect in AppContext).
//
// Where the sign-in dialog appears depends on what else is on the partner's
// page. With a converter frame it opens there, which is the only frame tall
// enough. Without one — their pricing, blog and help pages carry this button
// alone — it opens in a popup, because this iframe is sized to the button and
// would clip the dialog to nothing.
//
// Rendered when the URL carries ?embed=auth (see isAuthEmbed in App.js).

import React, { useCallback, useEffect, useRef } from 'react';
import { Button, Box } from '@mui/material';
import { useApp } from '../../context/AppContext';
import {
  requestFromConverterFrame, watchConverterPresence, REQUEST_AUTH, REQUEST_ACCOUNT,
} from '../../utils/embedChannel';
import { openAuthPopup } from '../../utils/authPopup';
import { radii } from '../../theme/colors';

export default function AuthButtonEmbed() {
  const {
    authOk, email, setAuthModalOpen, setAuthTab, handleManageAccount,
  } = useApp();

  // Whether a converter frame shares this page, learned at mount rather than at
  // click time: with no converter we open a popup, and window.open must run in
  // the same task as the click or the browser blocks it. Awaiting the 250 ms
  // handoff first would spend that user activation.
  const converterPresent = useRef(false);
  useEffect(() => watchConverterPresence(() => { converterPresent.current = true; }), []);

  const handleClick = useCallback(async () => {
    // Signed in: a dropdown would be clipped by this button-sized frame, so ask
    // the converter frame to show the account dialog (billing + logout). This
    // matters most with ?hideAuth=1, where this button is the only account
    // control on the page. A logout over there propagates back here via the
    // storage listener. Standalone fallback: the Stripe billing portal at top
    // level, which needs no room in this frame.
    if (authOk) {
      const handledElsewhere = await requestFromConverterFrame(REQUEST_ACCOUNT);
      if (!handledElsewhere) handleManageAccount();
      return;
    }
    // This frame is only as tall as the button, so prefer opening the dialog in
    // the converter frame on the same page.
    if (converterPresent.current) {
      const handledElsewhere = await requestFromConverterFrame(REQUEST_AUTH, { tab: 'signin' });
      if (handledElsewhere) return;
    }

    // No converter frame — nowhere on this page can show the dialog. The
    // partner sizes this iframe to the button (300x64 on their nav), so opening
    // it here would clip it to nothing and trap the visitor behind a backdrop
    // with no reachable Close. Give it its own window instead; their page stays
    // loaded underneath and the popup hands the session back when it is done.
    if (openAuthPopup('signin')) return;

    // Popup blocked. A plain tab is the next best thing — the visitor still
    // gets a usable sign-in, just not layered over the partner's page.
    try {
      if (window.open(`${window.location.origin}/?authPopup=1&tab=signin`, '_blank')) return;
    } catch (_) { /* fall through */ }

    // Everything refused. Open here as a last resort: cramped in a button-sized
    // frame, but better than a button that does nothing.
    setAuthTab('signin');
    setAuthModalOpen(true);
  }, [authOk, handleManageAccount, setAuthModalOpen, setAuthTab]);

  return (
    // inline-flex with no padding keeps the iframe's usable area tight to the
    // button, so the partner can size the frame to the control itself.
    <Box sx={{ display: 'inline-flex', bgcolor: 'transparent' }}>
      <Button
        id="embed-account-btn"
        variant="contained"
        color="secondary"
        size="small"
        onClick={handleClick}
        sx={{
          borderRadius: `${radii.control}px`, textTransform: 'uppercase',
          letterSpacing: '0.04em', fontSize: '0.8125rem', px: 3, py: 1.6,
          whiteSpace: 'nowrap',
        }}
      >
        {authOk ? (email || window.localStorage.getItem('userEmail') || 'Account') : 'Sign in / Create account'}
      </Button>
    </Box>
  );
}
